import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import DateRangePicker from 'components/dataPicker';
import { getCustomerOrderedProducts } from 'interfaces/customer';

type ProductRow = {
    productName: string;
    productRef: string;
    totalQuantity: number;
    totalPrice: number;
    orderCount: number;
};

const columnHelper = createColumnHelper<ProductRow>();

export default function OrderedProducts(props: { id: string }) {
    const { id } = props;
    const [sorting, setSorting] = useState<SortingState>([]);
    const [products, setProducts] = useState<ProductRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
        endDate: new Date(),
    });
    const [visibleItems, setVisibleItems] = useState(10);

    const fetchProducts = async () => {
        if (!id) return;
        setLoading(true);
        const res = await getCustomerOrderedProducts(id, filter);
        if (res) {
            setProducts(res);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchProducts();
    }, [filter, id]);

    const columns = [
        columnHelper.accessor('productRef', {
            id: 'productRef',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Référence
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {info.getValue() || '-'}
                </p>
            ),
        }),
        columnHelper.accessor('productName', {
            id: 'productName',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Produit
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {info.getValue()}
                </p>
            ),
        }),
        columnHelper.accessor('totalQuantity', {
            id: 'totalQuantity',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Quantité totale
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-medium text-navy-700 dark:text-white">
                    {Math.round(info.getValue() * 100) / 100}
                </p>
            ),
        }),
        columnHelper.accessor('orderCount', {
            id: 'orderCount',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Nb commandes
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-medium text-gray-600 dark:text-white">
                    {info.getValue()}
                </p>
            ),
        }),
        columnHelper.accessor('totalPrice', {
            id: 'totalPrice',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total HT
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
    ];

    const table = useReactTable({
        data: products,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleDateChange = (dates: any) => {
        const startDate = dates.startDate;
        const endDate = dates.endDate;
        setFilter({
            startDate,
            endDate,
        });
    };

    const totalQuantity = Math.round(products.reduce((sum, p) => sum + p.totalQuantity, 0) * 100) / 100;
    const totalPrice = Math.round(products.reduce((sum, p) => sum + p.totalPrice, 0) * 100) / 100;

    return (
        <Card extra={'w-full h-full px-6 pb-6 sm:overflow-x-auto border'}>
            <div className="mt-5 w-full px-[8px]">
                <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                        Produits commandés
                    </h4>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                    {products.length} produit{products.length !== 1 ? 's' : ''} différent{products.length !== 1 ? 's' : ''} commandé{products.length !== 1 ? 's' : ''} pour un total de{' '}
                    <span className="font-bold">{totalQuantity} unités</span> et{' '}
                    <span className="font-bold">{totalPrice.toFixed(2)} € HT</span>
                </p>
                <div className="mt-4">
                    <DateRangePicker
                        label=""
                        defaultStartDate={filter.startDate}
                        defaultEndDate={filter.endDate}
                        onChange={handleDateChange}
                    />
                </div>
            </div>

            <div className="mt-2 overflow-x-scroll xl:overflow-x-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-32">
                        <p className="text-gray-400 dark:text-white">
                            Chargement...
                        </p>
                    </div>
                ) : (
                    <>
                        <table className="w-full">
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr
                                        key={headerGroup.id}
                                        className="!border-px !border-gray-400"
                                    >
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <th
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className="cursor-pointer border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30"
                                                >
                                                    <div className="items-center justify-between text-xs text-gray-200">
                                                        {flexRender(
                                                            header.column.columnDef
                                                                .header,
                                                            header.getContext(),
                                                        )}
                                                        {{
                                                            asc: '',
                                                            desc: '',
                                                        }[
                                                            header.column.getIsSorted() as string
                                                        ] ?? null}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table
                                    .getRowModel()
                                    .rows.slice(0, visibleItems)
                                    .map((row) => {
                                        return (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => {
                                                    return (
                                                        <td
                                                            key={cell.id}
                                                            className="min-w-[90px] border-white/0 py-2 px-2"
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef
                                                                    .cell,
                                                                cell.getContext(),
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                        {table.getRowModel().rows.length === 0 && (
                            <div className="flex items-center justify-center h-32">
                                <p className="text-gray-400 dark:text-white">
                                    Aucun produit commandé sur cette période
                                </p>
                            </div>
                        )}
                        {table.getRowModel().rows.length > visibleItems && (
                            <div className="flex justify-center mt-4 pb-4">
                                <button
                                    onClick={() => setVisibleItems(visibleItems + 10)}
                                    className="px-4 py-2 text-sm font-medium text-toola-500 border border-toola-500 rounded-lg hover:bg-toola-50 transition-colors"
                                >
                                    Voir plus ({table.getRowModel().rows.length - visibleItems} restants)
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Card>
    );
}
